import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { authAPI, deviceTokenAPI, AUTH_ERROR_EVENT, AUTH_LOGIN_EVENT, clearCSRFToken, API_URL } from '../services/api';
import { unsubscribeFromPush } from '../utils/iosPush';
import { getErrorMessage } from '../utils/errorUtils';
import { User, AuthResponse } from '../types';
import { unwrapResponse } from '../utils/unwrapResponse';
import { queryClient } from '../lib/queryClient';
import { queryKeys } from '../hooks/queries/queryKeys';
import { isCancelledError } from '@tanstack/react-query';
import { isNativeApp } from '../utils/pwa';
import { clearPendingRegistrationToken, getPendingRegistrationToken } from '../utils/pendingRegistrationToken';

// ─── Linked Account Types (Spec 079) ────────────────────────────────────────

export interface LinkedAccount {
  userId: string;
  email: string;
  username?: string;
  name: string;
  role?: string;
  accessToken: string;
  refreshToken: string;
}

const LINKED_ACCOUNTS_KEY = 'linkedAccounts';

function getStoredLinkedAccounts(): LinkedAccount[] {
  try {
    const stored = localStorage.getItem(LINKED_ACCOUNTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function setStoredLinkedAccounts(accounts: LinkedAccount[]): void {
  localStorage.setItem(LINKED_ACCOUNTS_KEY, JSON.stringify(accounts));
}

// ─── Context Interface ──────────────────────────────────────────────────────

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  sessionMessage: string | null;
  /** Set when the current session is an admin impersonation session (Spec 197) */
  impersonatedBy?: number;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string, refreshToken?: string) => Promise<void>;
  register: (email: string, username: string, password: string, name: string, dateOfBirth: string, tosAccepted: boolean) => Promise<void>;
  verifyAge: (dateOfBirth: string) => Promise<void>;
  logout: () => void;
  clearSessionMessage: () => void;
  refreshUser: () => Promise<void>;
  // Fast user switch (Spec 079)
  linkedAccounts: LinkedAccount[];
  linkAccount: (email: string, password: string) => Promise<void>;
  unlinkAccount: (userId: string) => void;
  switchToAccount: (userId: string) => Promise<void>;
  /** Update a linked account's stored tokens in both localStorage and React state */
  updateLinkedAccount: (account: LinkedAccount) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function fetchUserProfile(): Promise<User> {
  const response = await authAPI.getProfile();
  return unwrapResponse<any>(response.data).user;
}

/**
 * Fetches the current user's profile through the shared TanStack Query
 * cache (Spec 296 Checkpoint 3). Uses the module-level `queryClient`
 * singleton's `fetchQuery()` rather than the `useQueryClient()` hook — this
 * file already imports the singleton for `.clear()` in logout()/
 * handleAuthError(), and reusing it here means AuthProvider adds ZERO new
 * `useQuery`/`useQueryClient` hook calls. `user` itself deliberately stays
 * plain `useState` (see its declaration below) rather than being derived
 * from a live `useQuery` observer — matching OrganizationContext.tsx's
 * `organization` (singular, current-identity) precedent, for the same
 * blank-page-flash reason (commit da8e630) and because `user` gates this
 * app's entire render tree via `loading`, an even higher-stakes case for
 * that risk than `organization` was.
 *
 * No live `useQuery` observer exists for this key anywhere in the file —
 * deliberately. Acceptance criterion 1 in the Spec 296 sprint contract asks
 * for AuthContext.tsx to be "useQuery-backed... mirroring
 * PushNotificationContext.tsx's shape," but PushNotificationContext's
 * mount-time observer fetches unconditionally BECAUSE its pre-migration
 * code also probed unconditionally on every mount. This file's profile
 * fetch never fires on a plain mount with an already-cached user+token —
 * only on loginWithToken(), the impersonation bootstrap, and an explicit
 * refreshUser() call. An `enabled: !!token` observer would add a genuine
 * `GET /auth/profile` on every app load that doesn't happen today — exactly
 * the silent cadence change Checkpoints 1 and 2 both explicitly refused to
 * introduce. (Superseded 2026-09-26 by bug-registry 0zzz-tpa: AuthProvider's
 * background sync now fetches on cold launch + foreground, deliberately and
 * outside this cache — see that effect.) Routing all three existing call sites through one shared
 * `queryClient.fetchQuery()` key is the faithful reading of "useQuery-backed"
 * for a file whose only server-fetched slice has no natural mount-fetch
 * shape — it gives the fetch the same primitives (retry policy, staleness
 * control, and eviction via the existing `queryClient.clear()` calls) as
 * every other migrated Context, without inventing a fake observer.
 *
 * staleTime: 0 on every call — all three sites want the freshest possible
 * data (a just-completed login, an impersonation bootstrap, or an explicit
 * refresh), never a cached hit within the global 30s default (lesson from
 * OrganizationContext.tsx's refreshOrganizations() HIGH finding). Single
 * UNSCOPED key (queryKeys.auth.profile(), no userId param) at all three
 * sites deliberately — two of them (loginWithToken, impersonation) fetch the
 * profile BEFORE the userId is known, so a userId-scoped key (unlike
 * queryKeys.auth.tosStatus()) isn't available at every call site; using it
 * at only some sites would silently split the cache into two entries and
 * defeat the point. Cross-user safety instead comes from staleTime: 0 here
 * plus the existing queryClient.clear() in logout()/handleAuthError().
 * retry: false — pre-migration authAPI.getProfile() had no retry wrapper at
 * any of its 3 call sites, and fetchQuery() does not inherit an observer's
 * own retry override (see OrganizationContext.tsx's refreshOrganizations()
 * for the same documented gotcha).
 *
 * Bounded retry (3 attempts) on CancelledError (bug-registry.md,
 * 197-impersonation E2E flake): moving this fetch into the shared
 * queryClient cache means it is no longer immune to a queryClient.clear()
 * that fires elsewhere in the app WHILE this fetch is in flight — e.g.
 * handleAuthError()'s clear() call, triggered by AUTH_ERROR_EVENT from a
 * totally unrelated 401 (any request, any component, same page). A raw
 * axios call (pre-migration) could not be cancelled that way.
 * queryClient.fetchQuery() rejects with a CancelledError in that case even
 * though the token being fetched for is perfectly valid — every caller (the
 * impersonation bootstrap, loginWithToken, refreshUser) would otherwise
 * treat that as "token revoked" and tear down a good session (the bootstrap
 * hard-redirects to /login and wipes a freshly issued, still-valid
 * impersonation token). Each clear() that caused a cancellation has already
 * happened by the time we catch it, so the next fetchQuery() call is a
 * genuine fresh attempt, not a repeat of the same race — but client.ts's
 * response interceptor calls dispatchAuthError() per failing request with
 * no dedup, so a burst of several unrelated 401s landing back-to-back could
 * in principle cancel more than one attempt in a row (skeptical review
 * finding, 2026-08-04): a single retry only covers ONE such cancellation.
 * Loop up to MAX_ATTEMPTS instead of retrying exactly once. If every
 * attempt is cancelled (a burst larger than MAX_ATTEMPTS — pathological),
 * or any attempt fails for a real reason, that error propagates — real
 * 401s (invalid/expired token) must always surface to callers on their
 * first, non-cancelled occurrence.
 */
const PROFILE_FETCH_MAX_ATTEMPTS = 3;

// Minimum gap between background profile syncs (cold launch + return to
// foreground) — bounds the extra GET /auth/profile under rapid app switching.
const PROFILE_SYNC_MIN_INTERVAL_MS = 60_000;

function fetchProfile(): Promise<User> {
  const fetchOnce = () => queryClient.fetchQuery({
    queryKey: queryKeys.auth.profile(),
    queryFn: fetchUserProfile,
    retry: false,
    staleTime: 0,
  });

  const attempt = (attemptsLeft: number): Promise<User> =>
    fetchOnce().catch((err) => {
      if (isCancelledError(err) && attemptsLeft > 1) {
        return attempt(attemptsLeft - 1);
      }
      throw err;
    });

  return attempt(PROFILE_FETCH_MAX_ATTEMPTS);
}

/** Extract the `impersonatedBy` numeric field from a JWT without verifying its signature */
function getImpersonatedByFromToken(jwtToken: string): number | undefined {
  try {
    const payloadB64 = jwtToken.split('.')[1];
    if (!payloadB64) return undefined;
    const decoded = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.impersonatedBy === 'number' ? decoded.impersonatedBy : undefined;
  } catch {
    return undefined;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [impersonatedBy, setImpersonatedBy] = useState<number | undefined>(undefined);
  // This tab's own identity, for the background profile sync (0zzz-tpa) — localStorage
  // is shared across tabs, so it can't tell whether THIS tab's user changed.
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  // Handle session expiry from API interceptor
  const handleAuthError = useCallback((event: Event) => {
    const customEvent = event as CustomEvent<{ message: string }>;
    const message = customEvent.detail?.message || 'Your session has expired. Please log in again.';

    // KNOWN GAP (2026-08-15 Notifications red-team fix, CRITICAL — same
    // shared-device push-leak class as logout()/switchToAccount(), NOT closed
    // here): this handler only runs after client.ts's reactive-401 refresh
    // attempt has ALSO failed (see client.ts's TOKEN_EXPIRED -> refresh ->
    // AUTH_ERROR_EVENT cascade), which means by this point BOTH the access
    // token (jwt.verify already threw TokenExpiredError server-side) and the
    // refresh token are dead. authenticateToken rejects an expired-signature
    // JWT outright, so there is no live credential left to authenticate a
    // deactivation call with -- unlike logout()/switchToAccount(), capturing
    // "the token we still have" doesn't help, because that token is already
    // provably invalid. Closing this residual gap needs a server-side
    // mechanism (deactivate push tokens tied to a session/refresh-token when
    // that session is revoked or found expired), tracked separately in
    // bug-registry.md rather than faked here with a call that would always
    // silently 401.

    // Clear auth state
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedOrganization');
    localStorage.removeItem('resolvedIdentity');
    // Clear linked accounts on session expiry
    localStorage.removeItem(LINKED_ACCOUNTS_KEY);
    clearCSRFToken();
    setToken(null);
    setUser(null);
    setLinkedAccounts([]);
    // Spec 292 safety review: the QueryClient is a module-level singleton that
    // outlives this SPA-navigation logout (no full page reload), so cached
    // per-user data (DMs, notifications, etc.) must be cleared explicitly or
    // the next user to authenticate in this tab can see it before their own
    // fetches land.
    queryClient.clear();

    // Set session message for display on login page
    setSessionMessage(message);
  }, []);

  useEffect(() => {
    // Spec 197: Bootstrap impersonation token from URL fragment before loadStoredAuth.
    // Admin opens /#impersonate=<TOKEN> in a new tab; we store the token then strip
    // the fragment from history so nginx access logs never capture the JWT.
    // Anchor to ^# so a stray ?impersonate= in the query string never matches —
    // query string is server-logged, fragment is not. Skeptical 2026-04-30.
    const hash = window.location.hash;
    const impersonateMatch = hash.match(/^#impersonate=([A-Za-z0-9._-]+)$/);

    // Spec 198: Bootstrap path must hydrate `user` before loadStoredAuth runs,
    // otherwise PillarLayout/ImpersonationBanner render without a target user.
    if (impersonateMatch) {
      const impersonationToken = impersonateMatch[1];
      localStorage.setItem('authToken', impersonationToken);
      localStorage.removeItem('refreshToken'); // No refresh for impersonation tokens
      localStorage.removeItem('user');         // Prevent stale cross-session bleed
      window.history.replaceState(null, '', window.location.pathname + window.location.search);

      (async () => {
        try {
          const userData = await fetchProfile();
          localStorage.setItem('user', JSON.stringify(userData));
          loadStoredAuth();
        } catch {
          // Token revoked, expired, or network failure — never strand the admin
          // in a half-authenticated state. Clear and redirect to /login.
          localStorage.removeItem('authToken');
          setLoading(false);
          window.location.href = '/login';
        }
      })();
    } else {
      loadStoredAuth();
    }

    // Listen for auth errors from API interceptor
    window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);

    return () => {
      window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
    };
  }, [handleAuthError]);

  // Bug-registry 0zzz-tpa: the cached `user` is otherwise only re-fetched on
  // login or after an edit on THIS device, so a profile edit made elsewhere
  // (web → native app) never showed up here, even across relaunches. Sync on
  // cold launch and whenever the app/tab returns to the foreground, throttled.
  // This deliberately adds the per-load GET /auth/profile that Spec 296 (see
  // fetchProfile()'s comment) declined to introduce as a silent side effect.
  useEffect(() => {
    let lastSyncAt: number | null = null;
    const syncProfile = async () => {
      // No cached user = logged out, or the impersonation bootstrap is already fetching.
      const userAtStart = localStorage.getItem('user');
      if (!localStorage.getItem('authToken') || !userAtStart) return;
      if (lastSyncAt !== null && Date.now() - lastSyncAt < PROFILE_SYNC_MIN_INTERVAL_MS) return;
      lastSyncAt = Date.now();
      try {
        // Direct call, NOT fetchProfile(): fetchQuery dedups onto an in-flight
        // fetch of the shared profile key, so a loginWithToken()/refreshUser()
        // issued mid-sync would receive THIS request's (old-token / pre-edit) result.
        // Raw axios, so queryClient.clear() in logout() can't cancel it — the
        // guards below are the only thing stopping a stale post-logout write.
        const userData = await fetchUserProfile();
        // Apply only if nothing else wrote the user meanwhile — a logout, account
        // switch, login or refreshUser() while this was in flight always wins.
        if (!localStorage.getItem('authToken') || localStorage.getItem('user') !== userAtStart) return;
        // ...and only for THIS tab's user: another tab's logout/login rewrites the
        // shared storage, and adopting that identity here would skip every reset
        // (cache clear, org/identity strip) a real identity transition performs.
        if (userRef.current?.id !== userData.id) return;
        const serialized = JSON.stringify(userData);
        if (serialized !== userAtStart) localStorage.setItem('user', serialized);
        // Compare against THIS tab's state, not storage: another same-account tab
        // may already have written this profile to storage without updating us.
        // Skip when unchanged — a new `user` object re-runs every [user] effect
        // (e.g. resets an in-progress username edit in account settings).
        if (JSON.stringify(userRef.current) !== serialized) setUser(userData);
      } catch (error) {
        // Background sync — cached user stays; a real 401 is handled by client.ts's
        // interceptor. Retry on the next foreground only for transient failures
        // (offline / 5xx) — a 403 for a suspended account must stay throttled.
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === undefined || status >= 500) lastSyncAt = null;
        console.warn('Background profile sync failed:', status ?? (error as Error)?.message);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void syncProfile();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Native: visibilitychange isn't reliably fired on app resume in every
    // WebView, so also listen for Capacitor's appStateChange (same as
    // nativeVersionGuard.ts). The throttle collapses a double fire.
    let cancelled = false;
    let removeAppStateListener: (() => void) | undefined;
    if (isNativeApp()) {
      import('@capacitor/app')
        .then(({ App }) => App.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
          if (isActive) void syncProfile();
        }))
        .then((handle) => {
          if (cancelled) handle.remove();
          else removeAppStateListener = () => { handle.remove(); };
        })
        .catch(() => { /* @capacitor/app unavailable — visibilitychange still covers resume */ });
    }

    void syncProfile();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      removeAppStateListener?.();
    };
  }, []);

  const loadStoredAuth = () => {
    try {
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        setImpersonatedBy(getImpersonatedByFromToken(storedToken));
      } else if (storedToken) {
        // Impersonation bootstrap case: token exists but no cached user yet
        setToken(storedToken);
        setImpersonatedBy(getImpersonatedByFromToken(storedToken));
      }

      // Load linked accounts
      setLinkedAccounts(getStoredLinkedAccounts());
    } catch (error) {
      console.error('Error loading auth:', error);
      // intentional: localStorage read on mount, UI shows logged-out state as safe fallback
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login(email, password);
      const data: AuthResponse = unwrapResponse<any>(response.data);

      localStorage.setItem('authToken', data.token);
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      localStorage.setItem('user', JSON.stringify(data.user));

      setToken(data.token);
      setUser(data.user);

      // SAFETY FIX (2026-08-05, root cause of a documented recurring
      // cross-user cache-leak class -- bug-registry.md, 9 live instances
      // found across this session): login()/register() are SPA navigations
      // (no page reload), so a same-tab account switch without an
      // intervening logout() previously left every per-user TanStack Query
      // cache entry from the prior session live for the newly authenticated
      // user to read until its own staleTime elapsed. logout()/
      // handleAuthError() already clear() for the identical reason (Spec
      // 292 safety review) -- this closes the gap at the other identity
      // transition instead of continuing to scope every affected key
      // individually.
      queryClient.clear();

      // Notify OrganizationContext to fetch orgs and handle auto-selection
      window.dispatchEvent(new Event(AUTH_LOGIN_EVENT));
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error) || 'Login failed');
    }
  };

  // Login with an existing JWT token (used after registration with age verification)
  const loginWithToken = async (authToken: string, refreshToken?: string) => {
    try {
      // Store the token
      localStorage.setItem('authToken', authToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      setToken(authToken);

      // Fetch user profile with the new token
      const userData = await fetchProfile();

      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      // SAFETY FIX -- see login()'s matching comment above, same root cause.
      queryClient.clear();

      // Notify OrganizationContext to fetch orgs and handle auto-selection
      window.dispatchEvent(new Event(AUTH_LOGIN_EVENT));
    } catch (error: unknown) {
      logout();
      throw new Error(getErrorMessage(error) || 'Failed to authenticate with token');
    }
  };

  const register = async (email: string, username: string, password: string, name: string, dateOfBirth: string, tosAccepted: boolean) => {
    try {
      const response = await authAPI.register(email, username, password, name, dateOfBirth, tosAccepted, getPendingRegistrationToken());
      const data: AuthResponse = unwrapResponse<any>(response.data);

      localStorage.setItem('authToken', data.token);
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      localStorage.setItem('user', JSON.stringify(data.user));

      setToken(data.token);
      setUser(data.user);
      clearPendingRegistrationToken(); // 0zzz-tzu review B: the account exists, the name hold it replaced is gone

      // SAFETY FIX -- see login()'s matching comment above, same root cause.
      queryClient.clear();
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error) || 'Registration failed');
    }
  };

  const verifyAge = async (dateOfBirth: string) => {
    try {
      await authAPI.verifyAge(dateOfBirth);
      // Refresh user data after verification
      await refreshUser();
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error) || 'Age verification failed');
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await fetchProfile();
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // intentional: non-critical background refresh, cached user data remains valid
    }
  };

  // Push notifications are subscribed per-device, not per-account -- a push
  // subscription/token created while logged in as this user stays valid
  // (and deliverable) on this device even after logging out or switching to
  // a different account, since nothing else deactivates it. On a shared
  // device or via Fast User Switch (no full logout), the next user to use
  // this device would keep receiving the outgoing user's pushes (DM
  // previews, chastity/keyholder status, task titles). Called from both
  // logout() and switchToAccount() -- best-effort, fire-and-forget, matching
  // unsubscribeFromPush()'s own "non-fatal" precedent -- so account
  // switching never blocks on it, but the outgoing user's device stops
  // receiving their pushes here (2026-08-15 Notifications red-team fix,
  // CRITICAL).
  const deactivatePushForOutgoingUser = () => {
    // Capture the still-live token synchronously, before the caller (logout()/
    // switchToAccount()) clears or swaps it. unsubscribeFromPush()'s own awaits
    // mean its network call fires several ticks later than this line runs, by
    // which point localStorage's authToken may no longer belong to the
    // outgoing user -- see unsubscribeFromPush's and deviceTokenAPI.unregister's
    // doc comments (2026-08-15 Notifications red-team fix, CRITICAL).
    const outgoingAuthToken = localStorage.getItem('authToken') ?? undefined;
    unsubscribeFromPush(outgoingAuthToken).catch(() => { /* non-fatal, matches iosPush.ts precedent */ });
    const storedFcmToken = localStorage.getItem('fcmToken');
    if (storedFcmToken) {
      deviceTokenAPI.unregister(storedFcmToken, outgoingAuthToken).catch(() => { /* non-fatal */ });
      localStorage.removeItem('fcmToken');
    }
  };

  const logout = () => {
    deactivatePushForOutgoingUser();
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedOrganization');
    localStorage.removeItem('resolvedIdentity');
    // Spec 079: Clear ALL linked accounts on logout
    localStorage.removeItem(LINKED_ACCOUNTS_KEY);
    clearCSRFToken();
    setToken(null);
    setUser(null);
    setLinkedAccounts([]);
    // Spec 292 safety review: see handleAuthError's matching comment above —
    // ordinary logout is also an SPA navigation, not a full reload, so the
    // QueryClient singleton must be cleared explicitly here too.
    queryClient.clear();
  };

  const clearSessionMessage = () => {
    setSessionMessage(null);
  };

  // ─── Fast User Switch (Spec 079) ───────────────────────────────────────────

  /**
   * Link another account for fast switching.
   * Calls POST /auth/link-account which validates both users have allow_fast_switch.
   */
  const linkAccount = async (email: string, password: string) => {
    try {
      const response = await authAPI.linkAccount(email, password);
      const { accessToken, refreshToken: rt, user: targetUser } = unwrapResponse<any>(response.data);

      const newLinked: LinkedAccount = {
        userId: targetUser.id,
        email: targetUser.email,
        username: targetUser.username,
        name: targetUser.name,
        role: targetUser.role,
        accessToken,
        refreshToken: rt,
      };

      // Replace if same user already linked, otherwise add
      const existing = getStoredLinkedAccounts();
      const filtered = existing.filter(a => a.userId !== targetUser.id);
      const updated = [...filtered, newLinked];
      setStoredLinkedAccounts(updated);
      setLinkedAccounts(updated);
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error) || 'Failed to link account');
    }
  };

  /**
   * Remove a linked account. Optionally revokes its refresh token.
   */
  const unlinkAccount = (userId: string) => {
    const existing = getStoredLinkedAccounts();
    const updated = existing.filter(a => a.userId !== userId);
    setStoredLinkedAccounts(updated);
    setLinkedAccounts(updated);
  };

  /**
   * Update a linked account's stored data (e.g. refreshed tokens) in both
   * localStorage and React state. Replaces the entry with matching userId.
   */
  const updateLinkedAccount = (account: LinkedAccount) => {
    const existing = getStoredLinkedAccounts();
    const updated = existing.map(a => a.userId === account.userId ? account : a);
    setStoredLinkedAccounts(updated);
    setLinkedAccounts(updated);
  };

  /**
   * Switch to a linked account.
   * Saves current account to linked list, then swaps the active tokens.
   * If the target's access token is expired, refreshes it first.
   */
  const switchToAccount = async (userId: string) => {
    const accounts = getStoredLinkedAccounts();
    const target = accounts.find(a => a.userId === userId);
    if (!target) {
      throw new Error('Account not found in linked accounts');
    }

    // Deactivate the outgoing user's push on this device before switching
    // (2026-08-15 Notifications red-team fix, CRITICAL) -- see
    // deactivatePushForOutgoingUser's doc comment. Fast User Switch is the
    // no-full-logout path this finding specifically named.
    deactivatePushForOutgoingUser();

    // Save current active account into linked accounts before switching
    const currentUser = user;
    const currentToken = localStorage.getItem('authToken');
    const currentRefreshToken = localStorage.getItem('refreshToken');

    if (currentUser && currentToken && currentRefreshToken) {
      const currentLinked: LinkedAccount = {
        userId: currentUser.id,
        email: currentUser.email,
        username: currentUser.username,
        name: currentUser.name,
        role: currentUser.role,
        accessToken: currentToken,
        refreshToken: currentRefreshToken,
      };
      // Replace if already in linked list, otherwise add
      const updatedAccounts = accounts.filter(a => a.userId !== currentUser.id && a.userId !== userId);
      updatedAccounts.push(currentLinked);
      setStoredLinkedAccounts(updatedAccounts);
      setLinkedAccounts(updatedAccounts);
    }

    // Try to use the target's access token; if expired, refresh first
    let activeAccessToken = target.accessToken;
    let activeRefreshToken = target.refreshToken;

    try {
      // Attempt a profile fetch to check if the access token is still valid
      const testResponse = await axios.get(`${API_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${activeAccessToken}` },
      });
      // Token is valid — update user data from the fresh profile
      const userData = unwrapResponse<any>(testResponse.data).user;
      localStorage.setItem('authToken', activeAccessToken);
      localStorage.setItem('refreshToken', activeRefreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.removeItem('selectedOrganization');
      clearCSRFToken();
      // Full reload to reset all cached component state for the new user
      window.location.href = '/helm';
    } catch {
      // Access token likely expired — try refresh
      try {
        const refreshResp = await axios.post(`${API_URL}/auth/refresh`, { refreshToken: activeRefreshToken });
        activeAccessToken = refreshResp.data.token;
        activeRefreshToken = refreshResp.data.refreshToken;

        // Now fetch profile with the new token
        const profileResp = await axios.get(`${API_URL}/auth/profile`, {
          headers: { Authorization: `Bearer ${activeAccessToken}` },
        });
        const userData = unwrapResponse<any>(profileResp.data).user;

        localStorage.setItem('authToken', activeAccessToken);
        localStorage.setItem('refreshToken', activeRefreshToken);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.removeItem('selectedOrganization');
        clearCSRFToken();

        // Update the linked account entry with refreshed tokens
        const updatedAccounts = getStoredLinkedAccounts().filter(a => a.userId !== userId);
        setStoredLinkedAccounts(updatedAccounts);
        setLinkedAccounts(updatedAccounts);

        // Full reload to reset all cached component state for the new user
        window.location.href = '/helm';
      } catch {
        // Refresh also failed — remove this linked account, it's no longer valid
        const cleaned = getStoredLinkedAccounts().filter(a => a.userId !== userId);
        setStoredLinkedAccounts(cleaned);
        setLinkedAccounts(cleaned);
        throw new Error('Linked account session has expired. Please re-link the account.');
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, sessionMessage, impersonatedBy,
      login, loginWithToken, register, verifyAge,
      logout, clearSessionMessage, refreshUser,
      linkedAccounts, linkAccount, unlinkAccount, switchToAccount,
      updateLinkedAccount,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
